import * as React from "react";
import Sys from "./Sys";

// The entries passed to the ResizeObserver callback is sometimes missing
// resized elements - therefore, we store our own array to check.
const overflowableRefs: Array<{
  ref: React.RefObject<HTMLElement>;
  setIsOverflowing: (isOverflowing: boolean) => void;
}> = [];

const resizeObserver = new ResizeObserver(() => {
  Sys.debounceMethod(
    () => {
      for (const { ref, setIsOverflowing } of overflowableRefs) {
        if (ref.current) {
          const { clientWidth, offsetWidth, scrollWidth } = ref.current;
          if (scrollWidth > clientWidth || scrollWidth > offsetWidth) {
            setIsOverflowing(true);
          } else {
            setIsOverflowing(false);
          }
        }
      }
    },
    "resize.overflowcheck",
    10
  );
});

const useIsOverflowing = <T extends HTMLElement>() => {
  const ref = React.useRef<T>(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  React.useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    } else {
      resizeObserver.observe(element);
      overflowableRefs.push({ ref, setIsOverflowing });

      // Clean up after ref.current changes
      return () => {
        let index = 0;
        for (let i = 0; i < overflowableRefs.length; i++) {
          if (overflowableRefs[i].ref === ref) {
            index = i;
            break;
          }
        }
        overflowableRefs.splice(index, 1);
        resizeObserver.unobserve(element);
      };
    }
  }, [ref.current]);

  return { isOverflowing, ref };
};

export default useIsOverflowing;
